import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Trash2, Undo2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ModelSelect } from "@/components/chat/Composer";
import { Button, Checkbox, ErrorText, Input, Label, Modal, Select, Spinner, Textarea } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { spaceApi, type HistoryItem, type Member, type Space } from "@/lib/spaces";

type Tab = "general" | "notes" | "members" | "rules" | "history";

const ACTIONS = { agent: "Model runs", notify: "Reminder", checkin: "Check-in" };

function General({ space, onClose }: { space: Space; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const owner = space.role === "owner";
  const [form, setForm] = useState({ name: space.name, description: space.description, instructions: space.instructions, settings: { ...space.settings } });
  const save = useMutation({
    mutationFn: () => api.patch(`/api/spaces/${space.id}`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["space", space.id] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
      qc.invalidateQueries({ queryKey: ["folders"] });
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/folders/${space.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spaces"] });
      qc.invalidateQueries({ queryKey: ["folders"] });
      navigate("/spaces");
    },
  });
  const set = (k: keyof Space["settings"], v: unknown) => setForm({ ...form, settings: { ...form.settings, [k]: v } });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  return (
    <form className="space-y-4" onSubmit={submit}>
      {!owner && <p className="text-sm text-zinc-500">Only the owner can change these.</p>}
      <fieldset disabled={!owner} className="space-y-4">
        <Label label="Name">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </Label>
        <Label label="Description" hint="What the space is for, in a line. The model reads it too.">
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Label>
        <Label label="Instructions" hint="How the model should work here. The model can propose changes; you approve them unless allowed below.">
          <Textarea rows={6} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
        </Label>
        <div className="space-y-2">
          <Checkbox label="The model may change the instructions without asking" checked={!!form.settings.model_edits_instructions} onChange={(e) => set("model_edits_instructions", e.target.checked)} />
          <Checkbox
            label="Notifications outside the app say only that there is something (for private spaces)"
            checked={!!form.settings.generic_notifications}
            onChange={(e) => set("generic_notifications", e.target.checked)}
          />
          <Checkbox label="Other chats may add records here (e.g. “add milk to the shopping list”)" checked={!!form.settings.exposed} onChange={(e) => set("exposed", e.target.checked)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="The day ends at" hint="For “today” in views and rules, if your days end after midnight.">
            <Select value={form.settings.day_ends ?? 0} onChange={(e) => set("day_ends", Number(e.target.value))}>
              {Array.from({ length: 13 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00{h === 0 ? " (midnight)" : ""}
                </option>
              ))}
            </Select>
          </Label>
          <Label label="Model for this space" hint="Preselected for chats and the quick box here.">
            <div className="flex h-10 items-center gap-2 rounded-lg border border-zinc-300 px-2 dark:border-zinc-700">
              <ModelSelect value={form.settings.model_id ?? null} onChange={(id) => set("model_id", id)} />
              {form.settings.model_id && (
                <button type="button" className="ml-auto text-xs text-zinc-500 hover:underline" onClick={() => set("model_id", undefined)}>
                  Clear
                </button>
              )}
            </div>
          </Label>
        </div>
      </fieldset>
      <ErrorText error={save.error || remove.error} />
      {owner && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            className="mr-auto text-red-600"
            onClick={() => confirm(`Delete the space "${space.name}" with all its data and files? This cannot be undone.`) && remove.mutate()}
          >
            <Trash2 size={16} /> Delete space
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      )}
    </form>
  );
}

function Notes({ space }: { space: Space }) {
  const qc = useQueryClient();
  const [text, setText] = useState(space.notes);
  const save = useMutation({
    mutationFn: () => api.patch(`/api/spaces/${space.id}`, { notes: text }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["space", space.id] }),
  });
  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-500">What the model keeps for later chats in this space. You can correct it here; changes show in the history and can be undone.</p>
      <Textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} />
      <ErrorText error={save.error} />
      <div className="flex justify-end">
        <Button disabled={save.isPending || text === space.notes} onClick={() => save.mutate()}>
          {save.isSuccess && text === space.notes ? "Saved" : "Save notes"}
        </Button>
      </div>
    </div>
  );
}

function Members({ space }: { space: Space }) {
  const qc = useQueryClient();
  const me = useMe().data;
  const navigate = useNavigate();
  const key = ["space", space.id, "members"];
  const members = useQuery({ queryKey: key, queryFn: () => api.get<Member[]>(`/api/spaces/${space.id}/members`) });
  const [who, setWho] = useState("");
  const add = useMutation({
    mutationFn: () => api.post(`/api/spaces/${space.id}/members`, { who }),
    onSuccess: () => {
      setWho("");
      qc.invalidateQueries({ queryKey: key });
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.delete(`/api/spaces/${space.id}/members/${userId}`),
    onSuccess: (_, userId) => {
      if (userId === me?.id) navigate("/spaces");
      else qc.invalidateQueries({ queryKey: key });
    },
  });
  const owner = space.role === "owner";
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">Members see the data and the home page and can change them. Their chats here stay their own.</p>
      {members.isLoading && <Spinner />}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {members.data?.map((m) => (
          <li key={m.user_id} className="flex items-center justify-between gap-3 py-2 text-sm">
            <span>
              {m.name} <span className="text-zinc-500">@{m.username}</span>
              {m.role === "owner" && <span className="ml-2 text-xs text-zinc-500">owner</span>}
            </span>
            {m.role === "member" && (owner || m.user_id === me?.id) && (
              <Button size="sm" variant="ghost" onClick={() => remove.mutate(m.user_id)}>
                {m.user_id === me?.id ? "Leave" : "Remove"}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {owner && (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (who.trim()) add.mutate();
          }}
        >
          <Input placeholder="Username or email address" value={who} onChange={(e) => setWho(e.target.value)} />
          <Button disabled={add.isPending}>Share</Button>
        </form>
      )}
      <ErrorText error={add.error || remove.error} />
    </div>
  );
}

function Rules({ space }: { space: Space }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-500">
        What the space does on its own. Ask in a chat here for a new one (e.g. “remind me at 21:00 if nothing is logged”), or add one under{" "}
        <Link to="/tasks" className="text-indigo-600 hover:underline dark:text-indigo-400">
          Tasks
        </Link>
        .
      </p>
      {space.rules.length === 0 && <p className="text-sm text-zinc-500">No rules yet.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {space.rules.map((r) => (
          <li key={r.id} className="py-2 text-sm">
            <Link to={`/tasks/${r.id}`} className="font-medium hover:underline">
              {r.name}
            </Link>
            <span className="text-zinc-500">
              {" "}
              · {ACTIONS[r.action]} · {r.when}
              {!r.enabled && " · off"}
              {r.last_status && ` · last: ${r.last_status}`}
            </span>
            {r.condition && (
              <p className="mt-0.5 text-xs text-zinc-500">
                Only when this finds {r.condition.when === "none" ? "nothing" : "something"}: <code className="font-mono">{r.condition.sql}</code>
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function History({ space }: { space: Space }) {
  const qc = useQueryClient();
  const key = ["space", space.id, "history"];
  const history = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => api.get<HistoryItem[]>(`/api/spaces/${space.id}/history${pageParam ? `?before=${pageParam}` : ""}`),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.length === 30 ? last[last.length - 1].id : undefined),
  });
  const undo = useMutation({
    mutationFn: (batch: string) => spaceApi.undo(space.id, batch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["space", space.id], exact: true });
    },
  });
  const items = history.data?.pages.flat() ?? [];
  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-500">Every change to the data, notes and home page, newest first. A change can be undone if nothing changed it again since.</p>
      <ErrorText error={undo.error || history.error} />
      {history.isLoading && <Spinner />}
      {!history.isLoading && items.length === 0 && <p className="text-sm text-zinc-500">No changes yet.</p>}
      <ul className="max-h-[50vh] divide-y divide-zinc-200 overflow-y-auto dark:divide-zinc-800">
        {items.map((h) => (
          <li key={h.batch} className="flex items-start gap-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className={clsx(h.undone && "text-zinc-400 line-through")}>{h.summary}</p>
              <p className="text-xs text-zinc-500">
                {h.actor}
                {h.source.startsWith("chat:") && (
                  <>
                    {" in "}
                    <Link to={`/c/${h.source.slice(5)}`} className="hover:underline">
                      a chat
                    </Link>
                  </>
                )}{" "}
                · {new Date(h.at).toLocaleString()}
              </p>
              {h.sql && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-zinc-500">SQL</summary>
                  <pre className="mt-1 max-h-40 overflow-auto rounded bg-zinc-50 p-2 font-mono text-xs whitespace-pre-wrap dark:bg-zinc-950">{h.sql}</pre>
                </details>
              )}
            </div>
            {h.undoable && !h.undone && (
              <Button size="sm" variant="ghost" disabled={undo.isPending} onClick={() => confirm(`Undo this change?\n\n${h.summary}`) && undo.mutate(h.batch)}>
                <Undo2 size={14} /> Undo
              </Button>
            )}
          </li>
        ))}
      </ul>
      {history.hasNextPage && (
        <Button size="sm" variant="secondary" disabled={history.isFetchingNextPage} onClick={() => history.fetchNextPage()}>
          Older
        </Button>
      )}
    </div>
  );
}

export default function SpaceSettings({ space, onClose }: { space: Space; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("general");
  const tabs: [Tab, string][] = [
    ["general", "General"],
    ["notes", "Notes"],
    ["members", "Members"],
    ["rules", "Rules"],
    ["history", "History"],
  ];
  return (
    <Modal open onClose={onClose} title={space.name} wide>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-zinc-200 dark:border-zinc-800">
        {tabs.map(([t, label]) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx("-mb-px shrink-0 border-b-2 px-3 py-1.5 text-sm", tab === t ? "border-indigo-600 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100")}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "general" && <General space={space} onClose={onClose} />}
      {tab === "notes" && <Notes space={space} />}
      {tab === "members" && <Members space={space} />}
      {tab === "rules" && <Rules space={space} />}
      {tab === "history" && <History space={space} />}
    </Modal>
  );
}
